import React from 'react';
import PropTypes from 'prop-types';
import {
  Link,
  Card,
  CardHeader,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { formatDateISO } from 'src/utils/date';
import { formatCurrency } from 'src/utils/currency';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
    '& .MuiTableCell-root': {
      border: 0,
    },
    '& .MuiTableRow-root': {
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
    },
  },
  fontWeightMedium: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  cardHeader: {
    color: theme.palette.secondary.main,
  },
  specialInstructionsValue: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: '0',
  },
  dispatchNotesValue: {
    maxWidth: '200px',
    width: '100%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
}));

function ServiceInfo({
  customerName,
  customerPhone,
  dispatchId,
  providerAcceptDate,
  providerCancelDate,
  isGoa,
  specialInstructions,
  requestedServiceType,
  jobType,
  isCashCall,
  isPartialPayment,
  dispatchInternalNotes,
  dispatchProviderNotes,
  dispatchDate,
  dispatchState,
  dispatchStatus,
  unloadedMileageRate,
  freeUnloadedMiles,
  milesUnloaded,
  loadedMileageRate,
  freeLoadedMiles,
  milesLoaded,
  onLoadedMileageClick,
  onUnloadedMileageClick,
  unloadedMileageAmount,
  loadedMileageAmount,
  isEditable,
}) {
  const classes = useStyles();

  return (
    <Card className={classes.card}>
      <CardHeader
        className={classes.cardHeader}
        title="Service Info"
      />
      <Divider />
      <Table>
        <TableBody>
          <TableRow>
            <TableCell className={classes.fontWeightMedium}>Dispatch Id - Date</TableCell>
            <TableCell align="right">
              <Typography variant="body2" color="textSecondary">
                {dispatchId} - {formatDateISO(dispatchDate)}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.fontWeightMedium}>Dispatch Status - State</TableCell>
            <TableCell align="right">
              <Typography variant="body2" color="textSecondary">
                {dispatchStatus} - {dispatchState}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.fontWeightMedium}>Job Type - Service Type</TableCell>
            <TableCell align="right">
              <Typography variant="body2" color="textSecondary">
                {jobType} - {requestedServiceType}
              </Typography>
            </TableCell>
          </TableRow>
          {providerAcceptDate ? (
            <TableRow>
              <TableCell className={classes.fontWeightMedium}>Provider Accept Date</TableCell>
              <TableCell align="right">
                <Typography variant="body2" color="textSecondary">
                  {formatDateISO(providerAcceptDate)}
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
          {providerCancelDate ? (
            <TableRow>
              <TableCell className={classes.fontWeightMedium}>Canceled Date</TableCell>
              <TableCell align="right">
                <Typography variant="body2" color="textSecondary">
                  {formatDateISO(providerCancelDate)}
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
          <TableRow>
            <TableCell className={classes.fontWeightMedium}>Is GOA?</TableCell>
            <TableCell align="right">
              <Typography variant="body2" color="textSecondary">
                {isGoa ? 'True' : 'False'}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.fontWeightMedium}>Is Cash Call?</TableCell>
            <TableCell align="right">
              <Typography variant="body2" color="textSecondary">
                {isCashCall ? 'True' : 'False'}
              </Typography>
            </TableCell>
          </TableRow>
          {isPartialPayment !== null && (
            <TableRow>
              <TableCell className={classes.fontWeightMedium}>Is Partial Payment?</TableCell>
              <TableCell align="right">
                <Typography variant="body2" color="textSecondary">
                  {isPartialPayment ? 'True' : 'False'}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell className={classes.fontWeightMedium}>Customer&apos;s Name/Phone</TableCell>
            <TableCell align="right">
              <Typography variant="body2" color="textSecondary">
                {customerName} / {customerPhone}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.fontWeightMedium}>
              {isEditable ?
              <Link component='button' variant="body2" onClick={onUnloadedMileageClick}>
                Unloaded Miles
              </Link> 
              :<>Unloaded Miles</>}
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" color="textSecondary">
                Total Miles: {milesUnloaded ?? 0}
                <br />
                Free Miles: {freeUnloadedMiles ?? 0}
                <br />
                Rate: {formatCurrency(unloadedMileageRate)}
                <br />
                Total Charge: {formatCurrency(unloadedMileageAmount)}

              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.fontWeightMedium}>
            {isEditable ?
              <Link component='button' variant="body2" onClick={onLoadedMileageClick}>
                Loaded Miles
              </Link> : <>Loaded Miles</>}
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" color="textSecondary">
                Total Miles: {milesLoaded ?? 0}
                <br />
                Free Miles: {freeLoadedMiles ?? 0}
                <br />
                Rate: {formatCurrency(loadedMileageRate)}
                <br />
                Total Charge: {formatCurrency(loadedMileageAmount)}
              </Typography>
            </TableCell>
          </TableRow>
          {specialInstructions ? (
            <TableRow>
              <TableCell className={classes.fontWeightMedium}>Special Instructions</TableCell>
              <TableCell align="right" className={classes.specialInstructionsValue}>
                <Tooltip
                  arrow
                  leaveDelay={200}
                  title={specialInstructions}
                >
                  <Typography variant="body2" color="textSecondary" noWrap >
                    {specialInstructions}
                  </Typography>
                </Tooltip>
              </TableCell>
            </TableRow>
          ) : null}
          {dispatchInternalNotes ? (
            <TableRow>
              <TableCell className={classes.fontWeightMedium}>Internal Notes</TableCell>
              <TableCell align="right" className={classes.dispatchNotesValue}>
                <Tooltip
                  arrow
                  leaveDelay={200}
                  title={dispatchInternalNotes}
                >
                  <Typography variant="body2" color="textSecondary" noWrap >
                    {dispatchInternalNotes}
                  </Typography>
                </Tooltip>
              </TableCell>
            </TableRow>
          ) : null}
          {dispatchProviderNotes ? (
            <TableRow>
              <TableCell className={classes.fontWeightMedium}>Provider Notes</TableCell>
              <TableCell align="right" className={classes.dispatchNotesValue}>
                <Tooltip
                  arrow
                  leaveDelay={200}
                  title={dispatchProviderNotes}
                >
                  <Typography variant="body2" color="textSecondary" noWrap >
                    {dispatchProviderNotes}
                  </Typography>
                </Tooltip>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </Card>
  );
}

ServiceInfo.propTypes = {
  customerName: PropTypes.string,
  customerPhone: PropTypes.string,
  dispatchId: PropTypes.string,
  providerAcceptDate: PropTypes.string,
  providerCancelDate: PropTypes.string,
  isGoa: PropTypes.bool,
  specialInstructions: PropTypes.string,
  requestedServiceType: PropTypes.string,
  jobType: PropTypes.string,
  isCashCall: PropTypes.bool,
  dispatchInternalNotes: PropTypes.string,
  dispatchProviderNotes: PropTypes.string,
  dispatchDate: PropTypes.string,
  dispatchState: PropTypes.string,
  dispatchStatus: PropTypes.string,
  unloadedMileageRate: PropTypes.number,
  freeUnloadedMiles: PropTypes.number,
  milesUnloaded: PropTypes.number,
  loadedMileageRate: PropTypes.number,
  freeLoadedMiles: PropTypes.number,
  milesLoaded: PropTypes.number,
  onLoadedMileageClick: PropTypes.func,
  onUnloadedMileageClick: PropTypes.func,
  unloadedMileageAmount: PropTypes.number,
  loadedMileageAmount: PropTypes.number,
  isEditable: PropTypes.bool,
};

export default ServiceInfo;
